// src/components/modules/HolidayCalendar.js
// Holiday calendar — Admin (role 3) and HR (role 4) maintain the company holiday list per year,
// with a separate calendar per country: India and United States (US is empty until holidays are added).
// The dashboard, Organisation view and Utilization read the India list:
//   General    → not a working day for anyone (not expected, not missed; hours worked = holiday hours)
//   Restricted → optional / regional; counts as a holiday only for people who log nothing that day

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus, FiEdit2, FiTrash2, FiUpload, FiCalendar, FiX, FiCheck } from "react-icons/fi";
import { fetchHolidayCalendar, saveHoliday, deleteHoliday, importHolidays } from "./dashboardApi";

export const HOLIDAY_EDITOR_ROLE_IDS = ["3", "4"]; // ADMIN, HR

const TYPES = ["General", "Restricted"];
const COUNTRIES = [
  { code: "IN", name: "India" },
  { code: "US", name: "United States" },
];
const countryName = (code) => COUNTRIES.find((c) => c.code === code)?.name || code;
const EMPTY_FORM = { id: 0, date: "", name: "", type: "General" };
const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

const pad = (n) => String(n).padStart(2, "0");
const longDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const weekday = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long" });
const isWeekend = (iso) => [0, 6].includes(new Date(`${iso}T00:00:00`).getDay());

// Pasted dates: 2026-01-15, 2026-01-15T00:00:00, 15-01-2026, 15/01/2026, 15.01.2026, 15-Jan-2026, 15 Jan 2026.
// Numeric day/month is read as DD/MM (Indian format).
export function parseDate(raw) {
  const v = String(raw || "").trim();
  let y, m, d;
  let r = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(v);
  if (r) [y, m, d] = [+r[1], +r[2], +r[3]];
  else if ((r = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(v))) [d, m, y] = [+r[1], +r[2], +r[3]];
  else if ((r = /^(\d{1,2})[-\s]([A-Za-z]{3})[A-Za-z]*[-\s,]+(\d{4})$/.exec(v))) [d, m, y] = [+r[1], MONTHS[r[2].toLowerCase()], +r[3]];
  else return null;
  if (!m) return null;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null; // e.g. 31-02
  return `${y}-${pad(m)}-${pad(d)}`;
}

// Rows copied from Excel (tab separated). Accepts "Date, Holiday, Type" or the
// company sheet layout "Sl No., Date, Holiday, Holiday Type". Header / title rows are skipped.
export function parsePasted(text) {
  const rows = [];
  const errors = [];
  String(text || "").split(/\r?\n/).forEach((line, i) => {
    if (!line.trim()) return;
    let cells = line.split(/\t|\s{2,}|,(?=\s*\S)/).map((c) => c.trim()).filter((c) => c !== "");
    if (cells.length >= 2 && /^\d{1,3}\.?$/.test(cells[0]) && parseDate(cells[1])) cells = cells.slice(1); // drop Sl No.
    const date = parseDate(cells[0]);
    if (!date) {
      // Title / header lines ("Holiday List For the Year 2026", "Sl No. Date Holiday ...") are ignored
      if (/\d{1,2}[-/.]\d{1,2}[-/.]\d{4}|\d{4}-\d{2}-\d{2}/.test(line)) errors.push(`Line ${i + 1}: can't read the date`);
      return;
    }
    const last = cells[cells.length - 1] || "";
    const hasType = cells.length >= 3 && /^(general|restricted|regional|optional)$/i.test(last);
    const name = (hasType ? cells.slice(1, -1) : cells.slice(1)).join(" ").trim();
    if (!name) { errors.push(`Line ${i + 1}: holiday name is missing`); return; }
    const type = hasType && /^(restricted|regional|optional)$/i.test(last) ? "Restricted" : "General";
    rows.push({ date, name, type });
  });
  return { rows, errors };
}

const TypeBadge = ({ type }) => (
  <span className={`inline-flex items-center h-6 px-2 rounded-full text-[12px] font-semibold ${
    type === "Restricted"
      ? "bg-[#F5F3FF] text-[#6D28D9] border border-dashed border-[#A78BFA]"
      : "bg-[#EDE9FE] text-[#5B21B6]"}`}>
    {type}
  </span>
);

const inputCls = "h-10 px-3 rounded-[10px] border border-[#D5DAE3] bg-white text-[14px] text-[#0F172A] focus:outline-none focus:border-[#1D5FD1] focus:ring-2 focus:ring-[#1D5FD1]/20";
const btnPrimary = "min-h-[40px] px-4 rounded-[10px] border-0 bg-[#1D5FD1] hover:bg-[#164AA6] text-white text-[14px] font-semibold cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed";
const btnSecondary = "min-h-[40px] px-4 rounded-[10px] border border-[#D5DAE3] bg-white text-[14px] font-medium text-[#0F172A] cursor-pointer hover:bg-[#F8FAFC] flex items-center gap-2 disabled:opacity-50";

export const HolidayCalendar = () => {
  const roleId = String(localStorage.getItem("emp_role_id") || "");
  const canEdit = HOLIDAY_EDITOR_ROLE_IDS.includes(roleId);
  const thisYear = new Date().getFullYear();

  const [country, setCountry] = useState(() => (new URLSearchParams(window.location.search).get("country") || "IN").toUpperCase() === "US" ? "US" : "IN");
  const [countryCounts, setCountryCounts] = useState({}); // { IN: 12, US: 0 } for the selected year
  const [year, setYear] = useState(thisYear);
  const [years, setYears] = useState([]);
  const [list, setList] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [notice, setNotice] = useState(null); // { kind: "ok" | "error", text }

  const [form, setForm] = useState(null); // null = closed, else EMPTY_FORM-shaped
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const [importOpen, setImportOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importCheck, setImportCheck] = useState(null); // dry-run result

  const load = useCallback((y, c) => {
    setState("loading");
    fetchHolidayCalendar(y, c)
      .then((d) => {
        setList(d.holidays);
        setYears(d.years);
        setCountryCounts(Object.fromEntries(d.countries.map((x) => [x.code, x.holidays])));
        setState("ready");
      })
      .catch((err) => { console.error("Holiday calendar load failed:", err); setState("error"); });
  }, []);
  useEffect(() => { load(year, country); }, [year, country, load]);

  const switchCountry = (c) => {
    if (c === country) return;
    setCountry(c);
    setForm(null); setConfirmId(null); setImportOpen(false); setPasteText(""); setImportCheck(null); setNotice(null);
    try {
      const url = new URL(window.location.href);
      if (c === "IN") url.searchParams.delete("country"); else url.searchParams.set("country", c);
      window.history.replaceState(null, "", url);
    } catch { /* address bar update is optional */ }
  };

  // Year choices: years in the table + this year + next year
  const yearOptions = useMemo(
    () => [...new Set([...years, thisYear, thisYear + 1, year])].sort((a, b) => a - b),
    [years, thisYear, year]
  );

  const counts = useMemo(() => ({
    general: list.filter((h) => h.type === "General").length,
    restricted: list.filter((h) => h.type === "Restricted").length,
    weekdayGeneral: list.filter((h) => h.type === "General" && !isWeekend(h.date)).length,
  }), [list]);

  const say = (kind, text) => setNotice({ kind, text });

  // ── Add / edit ──
  const formError = form && (
    !form.date ? "Pick a date."
      : Number(form.date.slice(0, 4)) !== year ? `The date must be in ${year}.`
        : !form.name.trim() ? "Enter the holiday name."
          : list.some((h) => h.date === form.date && h.id !== form.id) ? `That date is already in the ${countryName(country)} calendar.`
            : null);

  const submit = async (e) => {
    e.preventDefault();
    if (formError) return;
    setSaving(true);
    try {
      const msg = await saveHoliday({ ...form, name: form.name.trim(), country });
      say("ok", msg);
      setForm(null);
      load(year, country);
    } catch (err) {
      say("error", err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (h) => {
    try {
      say("ok", await deleteHoliday(h.id));
      setConfirmId(null);
      load(year, country);
    } catch (err) {
      say("error", err.message);
    }
  };

  // ── Import (paste from Excel) ──
  const parsed = useMemo(() => parsePasted(pasteText), [pasteText]);
  const otherYear = parsed.rows.filter((r) => Number(r.date.slice(0, 4)) !== year);

  const runImport = async (dryRun) => {
    setImportBusy(true);
    try {
      const res = await importHolidays(parsed.rows, { dryRun, country });
      if (dryRun) setImportCheck(res);
      else {
        say("ok", res.message);
        setImportOpen(false); setPasteText(""); setImportCheck(null);
        load(year, country);
      }
    } catch (err) {
      say("error", [err.message, ...(err.details || [])].join(" "));
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <div className="pt-6 px-[clamp(16px,3vw,40px)] pb-12 bg-[#F4F6FA] min-h-screen text-[#0F172A]" style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div className="max-w-[980px] mx-auto flex flex-col gap-5">

        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="m-0 text-[26px] font-semibold tracking-[-0.01em] flex items-center gap-2">
              <FiCalendar aria-hidden="true" className="text-[#7C3AED]" /> Holiday calendar
            </h1>
            <p className="m-0 text-[13px] text-[#5B6474]">
              Holidays are left out of expected hours and missed entries. Hours worked on a holiday show separately as holiday hours.
            </p>
          </div>
        </header>

        {/* Country calendars */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="tablist" aria-label="Country calendar" className="flex bg-white border border-[#D5DAE3] rounded-[10px] p-1">
            {COUNTRIES.map((c) => (
              <button key={c.code} type="button" role="tab" aria-selected={country === c.code} onClick={() => switchCountry(c.code)}
                className={`min-h-[34px] px-4 rounded-lg border-0 text-[13px] cursor-pointer flex items-center gap-2 ${country === c.code ? "bg-[#EDE9FE] text-[#5B21B6] font-semibold" : "bg-transparent text-[#3D4555] hover:bg-[#F4F6FA]"}`}>
                {c.name}
                <span className={`min-w-[22px] h-5 px-1.5 rounded-full text-[11px] leading-5 text-center ${country === c.code ? "bg-white text-[#5B21B6]" : "bg-[#F1F3F7] text-[#5B6474]"}`}>
                  {countryCounts[c.code] ?? 0}
                </span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[13px] text-[#5B6474] flex items-center gap-2">
              Year
              <select value={year} onChange={(e) => { setYear(Number(e.target.value)); setForm(null); setConfirmId(null); }} className={inputCls}>
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            {canEdit && (
              <>
                <button type="button" className={btnSecondary} onClick={() => { setImportOpen((x) => !x); setImportCheck(null); }}>
                  <FiUpload aria-hidden="true" /> Import list
                </button>
                <button type="button" className={btnPrimary} onClick={() => setForm({ ...EMPTY_FORM, date: "" })}>
                  <FiPlus aria-hidden="true" /> Add holiday
                </button>
              </>
            )}
          </div>
        </div>

        {country === "US" && (
          <div role="status" className="rounded-[12px] border border-[#D5DAE3] bg-white px-4 py-3 text-[13px] text-[#5B6474]">
            The US calendar is kept separately for US holidays. Hours, expected days and missed entries still use the India calendar.
          </div>
        )}

        {notice && (
          <div role={notice.kind === "error" ? "alert" : "status"}
            className={`flex items-start justify-between gap-3 rounded-[12px] px-4 py-3 text-[13px] border ${
              notice.kind === "error" ? "border-[#F6C9A8] bg-[#FFF4EC] text-[#7C2D12]" : "border-[#B7E4CF] bg-[#ECFDF5] text-[#065F46]"}`}>
            <span>{notice.text}</span>
            <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)} className="border-0 bg-transparent cursor-pointer text-inherit"><FiX /></button>
          </div>
        )}

        {!canEdit && (
          <div role="status" className="rounded-[12px] border border-[#D5DAE3] bg-white px-4 py-3 text-[13px] text-[#5B6474]">
            Only Admin and HR can change the holiday calendar.
          </div>
        )}

        {/* Add / edit form */}
        {canEdit && form && (
          <form onSubmit={submit} className="bg-white border border-[#E6E9EF] rounded-[14px] p-5 flex flex-col gap-4">
            <h2 className="m-0 text-[16px] font-semibold">{form.id ? `Edit ${countryName(country)} holiday` : `Add a ${countryName(country)} holiday to ${year}`}</h2>
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
              <label className="flex flex-col gap-1 text-[13px] text-[#3D4555]">
                Date
                <input type="date" className={inputCls} value={form.date} min={`${year}-01-01`} max={`${year}-12-31`}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              </label>
              <label className="flex flex-col gap-1 text-[13px] text-[#3D4555]">
                Holiday
                <input type="text" className={inputCls} value={form.name} maxLength={150} placeholder="e.g. Pongal"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </label>
              <label className="flex flex-col gap-1 text-[13px] text-[#3D4555]">
                Type
                <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
            </div>
            <p className="m-0 text-[12px] text-[#5B6474]">
              <strong>General</strong>: office closed for everyone. <strong>Restricted</strong>: optional / regional — it only counts as a
              holiday for people who don't log work that day.
              {form.date && isWeekend(form.date) && " This date is a weekend, so it won't change expected hours."}
            </p>
            {formError && form.date && <p role="alert" className="m-0 text-[13px] text-[#C2410C]">{formError}</p>}
            <div className="flex gap-2">
              <button type="submit" className={btnPrimary} disabled={!!formError || saving}>
                <FiCheck aria-hidden="true" /> {saving ? "Saving…" : form.id ? "Save changes" : "Add holiday"}
              </button>
              <button type="button" className={btnSecondary} onClick={() => setForm(null)}>Cancel</button>
            </div>
          </form>
        )}

        {/* Import from Excel */}
        {canEdit && importOpen && (
          <section className="bg-white border border-[#E6E9EF] rounded-[14px] p-5 flex flex-col gap-3">
            <h2 className="m-0 text-[16px] font-semibold">Import a {countryName(country)} holiday list</h2>
            <p className="m-0 text-[13px] text-[#5B6474]">
              Copy the rows from the holiday Excel (Date, Holiday, Holiday Type — the Sl No. column is fine too) and paste them below.
              Dates like 15-01-2026 are read as day-month-year. Dates already in the calendar are skipped.
            </p>
            <textarea value={pasteText} onChange={(e) => { setPasteText(e.target.value); setImportCheck(null); }} rows={8}
              placeholder={"1\t15-01-2027\tPongal\tGeneral\n2\t20-03-2027\tRamadan (Idul Fitr)\tRestricted"}
              className="w-full p-3 rounded-[10px] border border-[#D5DAE3] font-mono text-[13px] focus:outline-none focus:border-[#1D5FD1]" />

            {parsed.rows.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-[13px] text-[#3D4555]">
                  {parsed.rows.length} holiday{parsed.rows.length > 1 ? "s" : ""} found
                  {otherYear.length > 0 && <span className="text-[#C2410C]"> · {otherYear.length} not in {year} (they'll go into their own year)</span>}
                </span>
                <div className="max-h-[220px] overflow-auto border border-[#EEF0F4] rounded-[10px]">
                  <table className="w-full text-[13px] border-collapse">
                    <tbody>
                      {parsed.rows.map((r) => (
                        <tr key={r.date} className="border-b border-[#F1F3F7] last:border-0">
                          <td className="px-3 py-1.5 whitespace-nowrap">{longDate(r.date)}</td>
                          <td className="px-3 py-1.5 text-[#5B6474]">{weekday(r.date)}</td>
                          <td className="px-3 py-1.5">{r.name}</td>
                          <td className="px-3 py-1.5"><TypeBadge type={r.type} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {parsed.errors.length > 0 && (
              <ul className="m-0 pl-5 text-[13px] text-[#C2410C]">{parsed.errors.map((e) => <li key={e}>{e}</li>)}</ul>
            )}
            {importCheck && <p role="status" className="m-0 text-[13px] text-[#065F46]">{importCheck.message}</p>}

            <div className="flex flex-wrap gap-2">
              <button type="button" className={btnSecondary} disabled={!parsed.rows.length || parsed.errors.length > 0 || importBusy}
                onClick={() => runImport(true)}>
                Check
              </button>
              <button type="button" className={btnPrimary} disabled={!importCheck || importCheck.added === 0 || importBusy}
                onClick={() => runImport(false)}>
                {importBusy ? "Importing…" : importCheck ? `Import ${importCheck.added}` : "Import"}
              </button>
              <button type="button" className={btnSecondary} onClick={() => { setImportOpen(false); setPasteText(""); setImportCheck(null); }}>
                Cancel
              </button>
            </div>
          </section>
        )}

        {/* List */}
        <section className="bg-white border border-[#E6E9EF] rounded-[14px] overflow-hidden">
          <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 py-4 border-b border-[#EEF0F4]">
            <h2 className="m-0 text-[16px] font-semibold">{countryName(country)} · {year} holidays</h2>
            {state === "ready" && (
              <span className="text-[13px] text-[#5B6474]">
                {counts.general} general · {counts.restricted} restricted · {counts.weekdayGeneral} general holiday{counts.weekdayGeneral === 1 ? "" : "s"} on weekdays
              </span>
            )}
          </div>

          {state === "loading" && (
            <div className="p-5 flex flex-col gap-2">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-10 rounded-lg bg-[#F1F3F7] animate-pulse" />)}</div>
          )}
          {state === "error" && (
            <div role="alert" className="p-5 text-[13px] text-[#7C2D12]">
              Couldn't load the holiday calendar. <button type="button" onClick={() => load(year, country)} className="border-0 bg-transparent text-[#1D5FD1] font-semibold cursor-pointer">Try again</button>
            </div>
          )}
          {state === "ready" && list.length === 0 && (
            <p className="m-0 p-5 text-[13px] text-[#5B6474]">
              No {countryName(country)} holidays for {year} yet.{canEdit ? " Add them one by one or use Import list." : ""}
            </p>
          )}
          {state === "ready" && list.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[14px] border-collapse">
                <thead>
                  <tr className="text-left text-[12px] text-[#5B6474] bg-[#FAFBFC]">
                    <th className="px-5 py-2.5 font-medium">Date</th>
                    <th className="px-3 py-2.5 font-medium">Day</th>
                    <th className="px-3 py-2.5 font-medium">Holiday</th>
                    <th className="px-3 py-2.5 font-medium">Type</th>
                    {canEdit && <th className="px-5 py-2.5 font-medium text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {list.map((h) => (
                    <tr key={h.id} className="border-t border-[#F1F3F7]">
                      <td className="px-5 py-3 whitespace-nowrap font-medium">{longDate(h.date)}</td>
                      <td className={`px-3 py-3 whitespace-nowrap ${isWeekend(h.date) ? "text-[#8A93A3]" : "text-[#3D4555]"}`}>
                        {h.dayName || weekday(h.date)}{isWeekend(h.date) ? " (weekend)" : ""}
                      </td>
                      <td className="px-3 py-3">{h.name}</td>
                      <td className="px-3 py-3"><TypeBadge type={h.type} /></td>
                      {canEdit && (
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          {confirmId === h.id ? (
                            <span className="inline-flex items-center gap-2 text-[13px]">
                              Remove?
                              <button type="button" onClick={() => remove(h)} className="h-8 px-3 rounded-lg border-0 bg-[#C2410C] text-white font-semibold cursor-pointer">Remove</button>
                              <button type="button" onClick={() => setConfirmId(null)} className="h-8 px-3 rounded-lg border border-[#D5DAE3] bg-white cursor-pointer">Keep</button>
                            </span>
                          ) : (
                            <span className="inline-flex gap-1">
                              <button type="button" aria-label={`Edit ${h.name}`} title="Edit"
                                onClick={() => { setForm({ id: h.id, date: h.date, name: h.name, type: h.type }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                className="w-9 h-9 rounded-lg border-0 bg-transparent text-[#3D4555] cursor-pointer hover:bg-[#F4F6FA] flex items-center justify-center"><FiEdit2 /></button>
                              <button type="button" aria-label={`Remove ${h.name}`} title="Remove" onClick={() => setConfirmId(h.id)}
                                className="w-9 h-9 rounded-lg border-0 bg-transparent text-[#C2410C] cursor-pointer hover:bg-[#FFF4EC] flex items-center justify-center"><FiTrash2 /></button>
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default HolidayCalendar;